import * as React from 'react';
import { Caption2, tokens } from '@fluentui/react-components';
import { FeedbackButtons } from '@fluentai/feedback-buttons';
import { CareerHubModuleReactContext, useSharedStyles } from '@careerhub/common';
import { ResponseCategories, ResponseFeedback } from '@careerhub/api';
import { CopilotFeedbackForm } from './CopilotFeedbackForm';
import { Announced } from 'office-ui-fabric-react';

interface ICopilotMessageFooterProps {
  style?: React.CSSProperties;
  handleDialogSubmit: (
    commentResponse: string,
    responseFeedback: ResponseFeedback,
    responseCategories: ResponseCategories[]
  ) => boolean;
}

/** "AI-generated content may be incorrect" disclaimer and feedback buttons */
export const CopilotMessageFooter: React.FC<ICopilotMessageFooterProps> = (props) => {
  const style = props?.style ?? {};
  const sharedStyles = useSharedStyles();
  const [showFeedbackDialog, setShowFeedbackDialog] = React.useState(false);
  const [feedbackResponse, setFeedbackResponse] = React.useState<ResponseFeedback>(ResponseFeedback.NoFeedback);
  const [announceText, setAnnounceText] = React.useState('');
  const { telemetryClient } = React.useContext(CareerHubModuleReactContext);

  const [selected, setSelected] = React.useState<'positive' | 'negative' | undefined>(undefined);
  const positiveButton = React.useRef<HTMLButtonElement>(null);
  const negativeButton = React.useRef<HTMLButtonElement>(null);

  const handlePositiveFeedback = React.useCallback(() => {
    setFeedbackResponse(ResponseFeedback.ThumbsUp);
    setAnnounceText('');
    setShowFeedbackDialog(true);
    telemetryClient.logButtonClick('Copilot', 'CopilotFeedback', { feedback: ResponseFeedback.ThumbsUp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNegativeFeedback = React.useCallback(() => {
    setFeedbackResponse(ResponseFeedback.ThumbsDown);
    setAnnounceText('');
    setShowFeedbackDialog(true);
    telemetryClient.logButtonClick('Copilot', 'CopilotFeedback', { feedback: ResponseFeedback.ThumbsDown });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retainFocus = (response: ResponseFeedback, submitted: boolean) => {
    if (response === ResponseFeedback.ThumbsUp) {
      (submitted ? negativeButton : positiveButton).current?.focus();
    } else if (response === ResponseFeedback.ThumbsDown) {
      (submitted ? positiveButton : negativeButton).current?.focus();
    }
  };

  return (
    <>
      <div>
        {showFeedbackDialog && (
          <CopilotFeedbackForm
            isOpen={showFeedbackDialog}
            responseFeedback={feedbackResponse}
            handleSubmitTaskModule={(commentResponse, responseCategories) => {
              setSelected(feedbackResponse === ResponseFeedback.ThumbsUp ? 'positive' : 'negative');
              setAnnounceText('feedback submitted');
              props.handleDialogSubmit(commentResponse, feedbackResponse, responseCategories);
              setShowFeedbackDialog(false);
              retainFocus(feedbackResponse, true);
            }}
            handleOpenCloseTaskModule={(isOpen) => {
              setShowFeedbackDialog(isOpen);
              if (!isOpen) {
                retainFocus(feedbackResponse, false);
                setFeedbackResponse(ResponseFeedback.NoFeedback);
              }
            }}
          />
        )}
      </div>
      <div className={sharedStyles.flexSpaceBetween} style={{ paddingTop: '2px', alignItems: 'center', ...style }}>
        <Announced message={announceText} />
        <Caption2 style={{ color: tokens.colorNeutralForeground4 }}>
          This answer has been generated by AI based on the Role Library content. AI-generated content may be incorrect.
        </Caption2>
        <FeedbackButtons
          selected={selected}
          positiveFeedbackButton={{
            onClick: handlePositiveFeedback,
            disabled: selected === 'positive',
            ref: positiveButton,
          }}
          negativeFeedbackButton={{
            onClick: handleNegativeFeedback,
            disabled: selected === 'negative',
            ref: negativeButton,
          }}
        />
      </div>
    </>
  );
};
